import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAnalysis } from '../contexts/AnalysisContext';
import Dashboard from './Dashboard';
import { exportReportPdf, PdfReportData } from '../utils/exportPdf';
import './Pages.css';

export const ResultsPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { state, clearState } = useAnalysis();
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  // If no results available, redirect to upload page
  if (!state.results || state.sessionId !== sessionId) {
    return (
      <div className="results-page">
        <div className="results-container">
          <div className="no-results">
            <h2>No Results Available</h2>
            <p>The results you're looking for are not available or have expired.</p>
            <button 
              onClick={() => navigate('/')}
              className="submit-button"
            >
              Start New Analysis
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleNewAnalysis = () => {
    clearState();
    navigate('/');
  };

  const handleExportResults = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const chartEl = dashboardRef.current?.querySelector('.chart-container') as HTMLElement | null;
      const reportData: PdfReportData = {
        rankings: state.results!.rankings,
        summary: state.results!.summary,
        jobDescriptionText: state.jobDescription || undefined,
        processingTime: state.results!.processingTime,
        totalCandidates: state.results!.metadata?.totalCandidates,
      };
      await exportReportPdf(
        reportData,
        `resume-analysis-${sessionId}`,
        chartEl,
      );
    } catch (err) {
      console.error('PDF export failed', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="results-page">
      <div className="results-container">
        {/* Action buttons - moved to top right without white background */}
        <div className="results-actions-minimal">
          <button 
            onClick={handleExportResults}
            className="export-button-minimal"
            disabled={exporting}
          >
            {exporting ? '⏳ Generating PDF...' : '📥 Export PDF'}
          </button>
          <button 
            onClick={handleNewAnalysis}
            className="submit-button-minimal"
          >
            🔄 Analyze New Batch
          </button>
        </div>

        {/* Dashboard Component */}
        <div ref={dashboardRef}>
          <Dashboard 
            results={state.results}
          />
        </div>

        {/* Back to Top */}
        <div className="results-footer">
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="back-to-top"
          >
            ⬆️ Back to Top
          </button>
        </div>
      </div>
    </div>
  );
};